import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { config } from '../../services/Constants/AppConstants';
import {
  PASSWORD_MATCH_ERROR_MESSAGE,
  registerFields,
} from '../../services/Constants/FormConstants';
import { CreateAccountForm } from './CreateAccountForm';
import SocialAuthBlock from './SocialAuthBlock';
import { EDataKey, TData } from '../../types/forms';
import { SIGNUP, VERIFY_ORGANIZATION_TOKEN } from '../../services/ApolloClient';
import { useAuth, useApp } from '../../contexts';
import {
  cleanLocalStorageItems,
  getImpactFields,
  getLocalStorageItem,
  getOrgDetailsFromQueryString,
  getUtmFields,
  navigateWithReload,
  removeFields,
  getOrgParamsStatus,
} from '../../utils';
import { API } from '../../services/API/BaseAPI';
import './CreateAccount.scss';
import { InfoModal } from '../../components/InfoModal';
import { ROUTES } from '../../utils/routes';
import { STORAGE_KEYS } from '../../utils/storage';

function CreateAccount() {
  const fields = registerFields;
  const [formError, setFormError] = useState({});
  const [signup] = useMutation(SIGNUP);
  const [verifyOrganizationToken] = useMutation(VERIFY_ORGANIZATION_TOKEN);
  const { login } = useAuth();
  const { setLoading } = useApp();
  const [searchParams] = useSearchParams();
  const orgParamsStatus = getOrgParamsStatus(searchParams);
  const [organizationDetails] = useState(() => getOrgDetailsFromQueryString(searchParams));
  const [showInvalidOrgParamsModal, setShowInvalidOrgParamsModal] = useState(false);
  const [showInvalidUnumOriginModal, setShowInvalidUnumOriginModal] = useState(false);
  const referralId = searchParams.get('referral_id');
  const level = searchParams.get('level');
  const flLevel = searchParams.get('flLevel');
  localStorage.setItem('payment_plan', JSON.stringify(level));
  localStorage.setItem('referral_id', JSON.stringify(referralId));
  localStorage.setItem('flex_level', JSON.stringify(flLevel));

  const checkoutRedirect = (isEnterprise: boolean, isMetroPt: boolean) => {
    if (isEnterprise) {
      window.location.href = `${window.location.origin}/app/your-path/`;
      return;
    }

    const isProductRedirect = getLocalStorageItem('is_product_redirect');
    if (isProductRedirect) {
      cleanLocalStorageItems(['is_product_redirect']);
      window.location.href = `${window.location.origin}/products/checkout/?fromProduct=true`;
      return;
    }

    if (referralId) {
      cleanLocalStorageItems(['referral_id']);
      window.location.href = `${window.location.origin}/members/checkout/?referral_id=${referralId}`;
      return;
    }

    if (level) {
      const url = isMetroPt ? 'metro-pt' : 'checkout';
      const href = `${window.location.origin}/members/${url}/?level=${level}&isNew=true`;
      if (flLevel) {
        window.location.href = `${href}&flLevel=${flLevel}`;
      } else {
        window.location.href = href;
      }
      return;
    }

    const onboardingDataStr = localStorage.getItem(STORAGE_KEYS.ONBOARDING_DATA);
    if (onboardingDataStr) {
      try {
        const onboardingData = JSON.parse(onboardingDataStr);
        if (onboardingData?.recommendedStage) {
          navigateWithReload(ROUTES.RECOMMENDEDPROGRAM);
          return;
        }
        localStorage.removeItem(STORAGE_KEYS.ONBOARDING_DATA);
        return;
      } catch (error) {
        // Corrupted data - clear it and let the flow continue (post-purchase flow will handle onboarding)
        localStorage.removeItem(STORAGE_KEYS.ONBOARDING_DATA);
        return;
      }
    }
    throw Error('Checkout error. Please try later.');
  };

  const handleSignup = async (formData: Partial<TData>) => {
    try {
      setLoading(true);
      setFormError({});

      // Show modal if params are present but empty
      if (orgParamsStatus.anyEmpty) {
        setShowInvalidOrgParamsModal(true);
        setLoading(false);
        return;
      }

      if (formData.password !== formData.confirmPassword) {
        setFormError({
          message: PASSWORD_MATCH_ERROR_MESSAGE,
          fields: [EDataKey.password, EDataKey.confirmPassword],
        });
        setLoading(false);
        return;
      }

      const analyticsFields = getUtmFields();
      const impactFields = getImpactFields();

      // verify token using email
      if (organizationDetails && formData.email) {
        const verifyRes = await verifyOrganizationToken({
          variables: {
            organizationDetails,
            email: formData.email,
          },
        });

        if (verifyRes.data?.verifyOrganizationToken?.expired) {
          setFormError({
            message: 'This link has already been used. Please sign in instead.',
            fields: [EDataKey.email],
          });
          setLoading(false);
          return;
        }
      }

      // continue sign up
      const res = await signup({
        variables: {
          email: formData.email,
          password: formData.password,
          confirmPassword: formData.confirmPassword,
          analytics: analyticsFields,
          impact: impactFields,
          organizationDetails,
        },
      });

      removeFields();
      login(res.data?.signUp?.token);
      await API.post('auth-cookie');
      checkoutRedirect(res.data?.signUp?.isEnterprise, res.data?.signUp?.isMetroPt);
      setLoading(false);
    } catch (e: any) {
      setLoading(false);
      setFormError({
        message: e.message,
        fields: [EDataKey.email],
      });
    }
  };

  useEffect(() => {
    const ref = document.referrer || null;
    console.log('organizationDetails', organizationDetails);
    console.log('referrer', ref);

    if (organizationDetails) {
      const allowedOrigin = config.ALLOWED_UNUM_ORIGIN;

      const isValidOrigin = ref && ref.toLowerCase().startsWith(allowedOrigin.toLowerCase());

      if (!isValidOrigin) {
        setShowInvalidUnumOriginModal(true);
      }
    }
  }, [organizationDetails]);

  if (showInvalidUnumOriginModal) {
    return (
      <InfoModal
        isOpen={showInvalidUnumOriginModal}
        onOkClick={() => {
          window.location.href = config.ALLOWED_UNUM_ORIGIN;
        }}
        buttonLabel="Go to Care Hub"
        title="Access Restricted"
        info1="This offer is exclusive to Unum members and must be verified. Please return to the Unum Care Hub and click the Every Mother link from there to activate your free subscription."
      />
    );
  }

  return (
    <div className="register-login-container">
      <Helmet>
        <title>Create Account | Every Mother</title>
      </Helmet>
      {referralId && (
        <div className="referral-block">
          <h1 className="title">Activate your free 30-day guest pass.</h1>
          <p className="info">
            Exercise safely during pregnancy, heal after birth, and resolve common core and pelvic
            floor issues - all access for 30 days.
          </p>
        </div>
      )}
      <div className={`register-login-content ${referralId && 'referral-margin'}`}>
        <CreateAccountForm fields={fields} handleForm={handleSignup} formError={formError} />
        {!referralId && (
          <div className="form-links">
            <p>Already have an account? &nbsp;</p>
            <Link className="navigate-link" to="/login">
              sign in
            </Link>
          </div>
        )}

        {!orgParamsStatus.paramsPresent && <SocialAuthBlock />}

        {referralId && (
          <div className="referral-note">
            <p>
              By creating an account above, you consent to Every Mother’s Terms of Service and
              Privacy Policy. We use your email to provide you with news, updates and promotions.
            </p>
            <p>
              Free trial is for new subscribers only. After your 30-day trial ends, you will be
              charged a total of $59.99 for the first quarter. Your quarterly subscription will
              automatically renew for $59.99 every three months until you cancel. For more details
              on our payment terms and how to cancel, click{' '}
              <a href="https://every-mother.com/membership-policy">here</a>
            </p>
          </div>
        )}
      </div>
      <InfoModal
        isOpen={showInvalidOrgParamsModal}
        onOkClick={() => setShowInvalidOrgParamsModal(false)}
        title="Invalid Organization Link"
        info1="The link that you used to sign up is missing required information. Please contact the Unum Care Hub team for support."
      />
    </div>
  );
}

export default CreateAccount;
